// ============================================================
// AssistantDock — persistent GLOBAL assistant dock
//
// Mounted ONCE by Shell.tsx, fixed to the bottom of the viewport.
// Phase 1 is DETERMINISTIC client-side command routing — there is
// NO AI here and this component NEVER calls any /api/square/assistant
// route (it is not built). It matches operator input against the
// COMMAND_MODEL registry + a small keyword table and navigates.
//
// Honesty contract:
//   • Voice is Phase 2 → the mic button is DISABLED with a tooltip.
//   • Unrecognized input gets an honest inline reply, never a fake
//     AI answer.
//   • "summarize" / "where am i" reports the real current pathname
//     from useLocation — no model, no invention.
// ============================================================

import { useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, IconButton, Input, Tooltip } from '@square/ui'
import { COMMAND_MODEL } from './commandModel'
import { SHELL_TOKENS, text } from './styles'

// ------------------------------------------------------------
// Keyword → path routing table. Ordered; first phrase found in the
// (lowercased) input wins. These are intentionally explicit so the
// dock's behavior is auditable and never guesses.
// ------------------------------------------------------------

interface KeywordRoute {
  phrases: string[]
  path: string
  label: string
}

const KEYWORD_ROUTES: KeywordRoute[] = [
  { phrases: ['project command', 'projects', 'project'], path: '/square/projects', label: 'Project Command' },
  { phrases: ['workflows', 'workflow'], path: '/square/command-center/operations', label: 'Workflows' },
  { phrases: ['secure access', 'access codes', 'codes'], path: '/square/olivium/secure-access', label: 'Secure Access' },
  { phrases: ['receipts', 'receipt'], path: '/square/olivium/receipts', label: 'Receipts' },
  { phrases: ['access logs', 'sessions', 'session'], path: '/square/security/access-logs', label: 'Access Logs' },
  { phrases: ['dashboard', 'home'], path: '/square/dashboard', label: 'Dashboard' },
  { phrases: ['cockpit', 'command center'], path: '/square/command-center', label: 'Command Center' },
]

// Client-side "state" verbs — these never navigate; they report a
// deterministic, local fact (the current route).
const SUMMARIZE_PHRASES = ['summarize', 'where am i', 'where am i?']

// Example commands shown as clickable chips in the expanded drawer.
const EXAMPLE_COMMANDS: string[] = [
  'open secure access',
  'go to receipts',
  'show workflows',
  'open project command',
  'access logs',
  'where am i',
]

// ------------------------------------------------------------
// Pure matcher — deterministic, no side effects. Returns a resolved
// path (navigate), a summarize signal, or null (unrecognized).
// ------------------------------------------------------------

type Resolution =
  | { kind: 'navigate'; path: string; label: string }
  | { kind: 'summarize' }
  | { kind: 'none' }

function resolveCommand(raw: string, sections: typeof COMMAND_MODEL): Resolution {
  const q = raw.trim().toLowerCase()
  if (!q) return { kind: 'none' }

  // Client-side state verbs first — never navigate.
  if (SUMMARIZE_PHRASES.some((p) => q === p || q.includes(p))) {
    return { kind: 'summarize' }
  }

  // Exact / contained match against registry item + section labels.
  for (const section of sections) {
    if (q === section.label.toLowerCase()) {
      const first = section.items[0]
      if (first) return { kind: 'navigate', path: first.path, label: first.label }
    }
    for (const item of section.items) {
      const label = item.label.toLowerCase()
      if (q === label || q.includes(label)) {
        return { kind: 'navigate', path: item.path, label: item.label }
      }
    }
  }

  // Keyword table.
  for (const route of KEYWORD_ROUTES) {
    if (route.phrases.some((p) => q.includes(p))) {
      return { kind: 'navigate', path: route.path, label: route.label }
    }
  }

  return { kind: 'none' }
}

// ------------------------------------------------------------
// Style constants
// ------------------------------------------------------------

const BAR_HEIGHT = 44
const Z = 400 // above shell chrome, below modals (Modal uses higher)

// In-flow (NOT fixed): the dock is the last flex child of the shell column,
// rendered directly beneath the CommandBar, so the two STACK. A fixed/overlay
// dock would paint over and occlude the CommandBar's Execute surface.
const barShell: CSSProperties = {
  position: 'relative',
  zIndex: Z,
  flexShrink: 0,
  background: 'rgba(0, 0, 0, 0.94)',
  backdropFilter: 'blur(20px)',
  borderTop: `1px solid ${SHELL_TOKENS.borderAlt}`,
  color: SHELL_TOKENS.ink,
  fontFamily: 'var(--sc-font-ui, var(--font-body))',
}

const rowStyle: CSSProperties = {
  minHeight: BAR_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 14px',
}

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------

export default function AssistantDock() {
  const navigate = useNavigate()
  const location = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)

  const [value, setValue] = useState('')
  const [expanded, setExpanded] = useState(false)
  // Deterministic reply line rendered below the bar (state facts /
  // honest "not built yet" copy). Never an AI answer.
  const [reply, setReply] = useState<string | null>(null)

  const sections = useMemo(() => COMMAND_MODEL, [])

  function run(command: string) {
    const resolution = resolveCommand(command, sections)

    if (resolution.kind === 'navigate') {
      setReply(null)
      setValue('')
      navigate(resolution.path)
      return
    }

    if (resolution.kind === 'summarize') {
      setReply(`You are at ${location.pathname}. (Client-side fact — no AI. Voice + AI land in Phase 2.)`)
      setValue('')
      return
    }

    // Unrecognized — honest, no fabricated answer.
    setReply(
      'Voice + AI land in Phase 2. For now I navigate the operating model — try: open secure access, or go to receipts.',
    )
  }

  function onSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    run(value)
  }

  function onChipClick(command: string) {
    setValue(command)
    run(command)
    inputRef.current?.focus()
  }

  return (
    <div style={barShell} role="region" aria-label="Square Code Command Center assistant dock">
      {/* Reply line — deterministic client-side output only. */}
      {reply && (
        <div
          style={{
            padding: '8px 14px',
            borderBottom: `1px solid ${SHELL_TOKENS.border}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <span style={{ ...text.micro, color: SHELL_TOKENS.cyan, paddingTop: 1 }}>DOCK</span>
          <span style={{ ...text.body, fontSize: 13, color: SHELL_TOKENS.inkDim }}>{reply}</span>
          <button
            type="button"
            onClick={() => setReply(null)}
            aria-label="Dismiss reply"
            style={{
              marginLeft: 'auto',
              background: 'transparent',
              border: 'none',
              color: SHELL_TOKENS.inkFaint,
              cursor: 'pointer',
              fontFamily: 'var(--sc-font-mono, var(--font-mono))',
              fontSize: 12,
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Expanded drawer — example commands as clickable chips. */}
      {expanded && (
        <div
          style={{
            padding: '12px 14px',
            borderBottom: `1px solid ${SHELL_TOKENS.border}`,
            background: SHELL_TOKENS.surface,
          }}
        >
          <div style={{ ...text.micro, marginBottom: 8, color: SHELL_TOKENS.inkFaint }}>
            EXAMPLE COMMANDS · CLIENT-SIDE NAVIGATION ONLY
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {EXAMPLE_COMMANDS.map((cmd) => (
              <button
                key={cmd}
                type="button"
                onClick={() => onChipClick(cmd)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '5px 12px',
                  borderRadius: 999,
                  background: SHELL_TOKENS.surfaceAlt,
                  border: `1px solid ${SHELL_TOKENS.borderAlt}`,
                  color: SHELL_TOKENS.inkDim,
                  fontSize: 12,
                  fontFamily: 'var(--sc-font-mono, var(--font-mono))',
                  cursor: 'pointer',
                  letterSpacing: '0.02em',
                }}
              >
                {cmd}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Collapsed / always-present bar. */}
      <form style={rowStyle} onSubmit={onSubmit}>
        <span
          aria-hidden
          style={{
            ...text.micro,
            color: SHELL_TOKENS.cyan,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          ⌘ COMMAND
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Input
            ref={inputRef}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
            placeholder="Ask or command the Square Code Command Center…"
            aria-label="Ask or command the Square Code Command Center"
            style={{ padding: '7px 12px', fontSize: 13 }}
          />
        </div>

        <Button type="submit" variant="primary" size="sm" disabled={!value.trim()}>
          Run
        </Button>

        {/* Mic — Phase 2. Disabled, honest tooltip. */}
        <Tooltip label="Voice arrives in Phase 2" side="top">
          <IconButton
            size="sm"
            variant="ghost"
            disabled
            aria-label="Voice input (arrives in Phase 2)"
          >
            <span aria-hidden style={{ fontSize: 13 }}>🎤</span>
          </IconButton>
        </Tooltip>

        {/* Expand / collapse toggle. */}
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={expanded ? 'Collapse assistant dock' : 'Expand assistant dock'}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <span aria-hidden style={{ fontSize: 12, fontFamily: 'var(--sc-font-mono, var(--font-mono))' }}>
            {expanded ? '▾' : '▴'}
          </span>
        </IconButton>
      </form>
    </div>
  )
}
